import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MenuItem,
  Typography,
  Container,
  Table,
  Card,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputLabel,
  FormControl,
  Stack,
  Select,
  Divider,
  TextField,
  InputAdornment,
  TableBody,
  LinearProgress,
  IconButton
} from '@mui/material';
import '../css/GlobalStyles.css';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { LoadingButton } from '@mui/lab';
import * as XLSX from 'xlsx';
import { MerchantsMoreMenu } from '../components/_dashboard/soundbox';
import {
  CheckMerchantVpaDuplicates,
  GetMerchantVpas,
  UpdateMerchantVpaApprovalStatus
} from '../network/Merchants';
// components
import Page from '../components/Page';
import { useAuth } from '../components/customContextProviders/AuthContext';
import { ConfirmDialog, NoDataAvailable } from '../components/_dashboard/app';
import {
  BomDeviceRequestFormModal,
  IOBDeviceRequestModal,
  UjnDeviceRequestModal,
  NewNoxDeviceRequestFormModal,
  KagbDeviceRequestModal,
  NewNpstDeviceRequestFormModal,
  GBCBDeviceRequestModal
} from '../components/_dashboard/merchantforms';
import { SnackBarAlert, CustomPagination } from '../components/HelperComponents';
import { flattenMerchantVpaRow, parseValidationErrorMessage } from '../utils/Utilities';
import { APP_CONSTANTS, MERCHANT_MORE_MENU_ACTIONS } from '../URLConstants';
import DashboardNavbar from '../layouts/dashboard/DashboardNavbar';
import DashboardSidebar from '../layouts/dashboard/DashboardSidebar';
import HastiDeviceRequestModal from 'src/components/_dashboard/merchantforms/NewHastiDeviceRequestFormModal';
// ----------------------------------------------------------------------

const FIRST_PAGE = 1;
const MAX_SELECTION = 10000;

// The status dropdown keeps its existing values; the merchant-vpa queue filters on
// `approvalStatus`, where an already processed request is an APPROVED one.
const STATUS_TO_APPROVAL_STATUS = {
  PENDING: 'PENDING',
  ERROR: 'ERROR',
  PROCESSED: 'APPROVED'
};

// Same where-clause shape the merchant-vpa list expects: flat search key + approvalStatus.
function buildVerificationWhere(status, filterType, value) {
  let searchFilter = {};
  if (value) {
    if (filterType === 'vpa') searchFilter = { vpa: value.toLowerCase() };
    else if (filterType === 'accountNumber') searchFilter = { 'details.uniqueRef': value };
    else searchFilter = { [filterType]: value };
  }
  return {
    ...searchFilter,
    approvalStatus: STATUS_TO_APPROVAL_STATUS[status] || status
  };
}

const getFailedResults = (response) =>
  response?.data?.failureCount > 0
    ? (response?.data?.results || []).filter((r) => !r.success).map((r) => r.error)
    : [];

export default function MerchantOnboarding() {
  const {
    fetchBranchesList,
    accountDetails,
    regionsList,
    fetchRegionList,
    fetchTaxonomyDetails,
    branchesList,
    sessionUserData,
    userLoginOrg,
    fetchLanguages,
    languageList
  } = useAuth();

  const DEVICE_REQUEST_COMPONENTS = {
    [APP_CONSTANTS?.CBI_CLIENT_IDENTIFIER]: NewNoxDeviceRequestFormModal,
    [APP_CONSTANTS?.IOB_CLIENT_IDENTIFIER]: IOBDeviceRequestModal,
    [APP_CONSTANTS?.UJN_CLIENT_IDENTIFIER]: UjnDeviceRequestModal,
    [APP_CONSTANTS?.KAGB_CLIENT_IDENTIFIER]: KagbDeviceRequestModal,
    [APP_CONSTANTS?.NPST_CLIENT_IDENTIFIER]: NewNpstDeviceRequestFormModal,
    [APP_CONSTANTS?.GBCB_CLIENT_IDENTIFIER]: GBCBDeviceRequestModal,
    [APP_CONSTANTS?.HASTI_CLIENT_IDENTIFIER]: HastiDeviceRequestModal
  };

  const DeviceRequestModal = DEVICE_REQUEST_COMPONENTS[userLoginOrg] || null;

  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(FIRST_PAGE);
  // Component state (not module scope) so it can't leak across mounts or re-renders.
  const [hasNextPage, setHasNextPage] = useState(true);

  const [isDeviceRequestModalOpen, setIsDeviceRequestModalOpen] = useState(false);
  const [filterType, setFilterType] = useState('phone');
  const [filterValue, setFilterValue] = useState('');

  const [openConfirm, setOpenConfirm] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    loading: false,
    message: '',
    note: false,
    onConfirm: () => { },
    onCancel: () => setOpenConfirm(false),
    confirmText: ''
  });

  const [selection, setSelection] = useState({});
  const [selectedMerchant, setSelectedMerchant] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('PENDING');
  const [approvedArray, setApprovedArray] = useState([]);
  const [rejectedArray, setRejectedArray] = useState([]);
  const [isApproveEnabled, setIsApproveEnabled] = useState(false);

  const [audioCubeMerchantListData, setAudioCubeMerchantListData] = useState([]);
  const [audioCubeMerchantListDataLoading, setAudioCubeMerchantListDataLoading] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const where = useMemo(
    () => buildVerificationWhere(status, filterType, filterValue),
    [status, filterType, filterValue]
  );

  const handleDownloadExcel = useCallback(async () => {
    setIsDownloadingExcel(true);
    try {
      const response = await GetMerchantVpas(page, 10, where);
      const currentViewData = (response?.data?.data || []).map(flattenMerchantVpaRow);

      const worksheetData = currentViewData.map((row) => ({
        Name: row?.name || '',
        Phone: row?.phone || '',
        Status: row?.status || '',
        'Error Message': row?.errorMessage || '',
        State: row?.state || '',
        City: row?.city || '',
        Pincode: row?.pincode || '',
        'Merchant Id': row?.rawRowData?.UUID || row?.mid || '',
        MID: row?.mid || '',
        'Branch ID': row?.branchId || '',
        'Account No.': row?.details?.uniqueRef || '',
        'Onboarding Type': row?.details?.onBoardingType || '',
        'Account Type': row?.details?.accountType || '',
        'Is Verified': row?.details?.isVerified || '',
        'Merchant Genre': row?.details?.merchantGenre || '',
        'GST No.': row?.details?.gstIn || '',
        'Merchant Category Code': row?.details?.mcc || ''
      }));

      const ws = XLSX.utils.json_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Merchant Onboarding');
      XLSX.writeFile(wb, 'MerchantOnboardingList.xlsx');
    } catch (error) {
      setSnackBarMetas((prevState) => ({
        ...prevState,
        title: 'Failed to download current view. Please try again.',
        severity: 'error'
      }));
      setIsSnackBarAlertOpen(true);
    } finally {
      setIsDownloadingExcel(false);
    }
  }, [page, where]);
  useEffect(() => {
    fetchMerchants(page, 10, where, 'LOAD');
    return () => {
      setAudioCubeMerchantListData([]);
    };
  }, []);

  function onNewDeviceRequestModalAction(d) {
    alert(d);
    fetchMerchants(page, 10, where, 'SEARCH');
  }

  const handleChangePage = (data, customPage) => {
    let newPage = page;
    if (data === 'NEXT') newPage += 1;
    else newPage -= 1;

    if (customPage) {
      setPage(customPage);
      fetchMerchants(customPage, 10, where, 'SEARCH');

      return;
    }
    setPage(newPage);
    fetchMerchants(newPage, 10, where, 'SEARCH');
  };

  function fetchMerchants(pageNo, limit, where, type) {
    setAudioCubeMerchantListDataLoading(true);
    GetMerchantVpas(pageNo, limit, where)
      .then((res) => {
        setAudioCubeMerchantListDataLoading(false);
        setHasNextPage(Boolean(res?.data?.hasNextPage));
        const rows = (res?.data?.data || []).map(flattenMerchantVpaRow);

        if (type === 'LOAD') setAudioCubeMerchantListData((prevList) => [...prevList, ...rows]);
        else if (type === 'SEARCH') {
          setAudioCubeMerchantListData([]);
          setAudioCubeMerchantListData(rows);
        }
      })
      .catch((_error) => {
        setAudioCubeMerchantListDataLoading(false);
        if (type === 'SEARCH') {
          setAudioCubeMerchantListData([]);
        }
      });
  }

  const handleStatusClickChange = (event) => {
    setStatus(event.target.value);
    setPage(FIRST_PAGE);
    fetchMerchants(
      FIRST_PAGE,
      10,
      buildVerificationWhere(event.target.value, filterType, filterValue),
      'SEARCH'
    );
  };

  // Approve icon selection handling and then pushes selected Row's packageId to Array
  const handleApprove = (packageId) => {
    if (approvedArray.length + rejectedArray.length >= MAX_SELECTION) {
      setSnackBarMetas((prevState) => ({
        ...prevState,
        title: `Selection limit reached. Maximum allowed is ${MAX_SELECTION}.`,
        severity: 'warning'
      }));
      setIsSnackBarAlertOpen(true);

      return;
    }
    setSelection((prev) => ({ ...prev, [packageId]: 'approve' }));

    setApprovedArray((prev) => {
      const updatedArray = [...prev, packageId].filter(
        (id, index, self) => self.indexOf(id) === index
      ); // Add to approved
      return updatedArray;
    });

    setRejectedArray((prev) => prev.filter((id) => id !== packageId)); // Remove from rejected if exists
  };

  // RejectIcon selection handling and then pushes selected row's packageId to Array
  const handleReject = (packageId) => {
    if (approvedArray.length + rejectedArray.length >= MAX_SELECTION) {
      setSnackBarMetas((prevState) => ({
        ...prevState,
        title: `Selection limit reached. Maximum allowed is ${MAX_SELECTION}.`,
        severity: 'warning'
      }));
      setIsSnackBarAlertOpen(true);
      return;
    }

    setSelection((prev) => ({ ...prev, [packageId]: 'reject' }));

    setRejectedArray((prev) => {
      const updatedArray = [...prev, packageId].filter(
        (id, index, self) => self.indexOf(id) === index
      ); // Add to rejected
      return updatedArray;
    });

    setApprovedArray((prev) => prev.filter((id) => id !== packageId));
  };

  function handleMerchantDuplicacies(rowIds, noteValue) {
    const wanted = new Set(rowIds);
    const phones = [
      ...new Set(
        audioCubeMerchantListData
          .filter((row) => wanted.has(row?.id))
          .map((row) => row?.phone)
          .filter(Boolean)
      )
    ];
    if (phones.length === 0) {
      handleApprovalRejection(noteValue);
      return;
    }

    const isCbi = userLoginOrg === APP_CONSTANTS?.CBI_CLIENT_IDENTIFIER;
    CheckMerchantVpaDuplicates(phones)
      .then((matches) => {
        // The records being approved come back as matches on their own number; only the
        // other, already-live records are worth flagging.
        const errors = matches
          .map(({ value, records }) => {
            const existing = (records || []).filter((record) => !wanted.has(record?.id));
            if (existing.length === 0) return null;
            // CBI's VPA is generated server-side, so the account number identifies it instead.
            const identifiers = existing
              .map((record) => (isCbi ? record?.uniqueRef : record?.vpa))
              .filter(Boolean)
              .join(', ');
            const noun = isCbi ? 'merchant account' : 'merchant VPA';
            const plural = existing.length === 1 ? '' : 's';
            return `Phone ${value} is already associated with ${existing.length} ${noun}${plural}: ${identifiers}`;
          })
          .filter(Boolean);
        if (errors.length > 0) {
          // Collect all error messages into one block of text
          const errorMsg = errors.map((e, idx) => `${idx + 1}. ${e}`).join('\n');
          setDialogConfig({
            loading: false,
            message: `${errorMsg}.\n\nDo you still want to proceed?`,
            note: false,
            onConfirm: () => {
              handleApprovalRejection(noteValue);
            },
            onCancel: () => setOpenConfirm(false),
            confirmText: 'Proceed'
          });

          setOpenConfirm(true);
        } else {
          handleApprovalRejection(noteValue);
        }
      })
      .catch((err) => {
        handleApprovalRejection(noteValue);
      });
  }

  const handleSumitDecisionButton = (noteValue) => {
    setDialogConfig((prev) => ({
      ...prev,
      loading: true
    }));
    if (approvedArray.length > 0) {
      handleMerchantDuplicacies(approvedArray, noteValue);
    } else {
      handleApprovalRejection(noteValue);
    }
  };

  function handleApprovalRejection(noteValue) {
    setDialogConfig((prev) => ({
      ...prev,
      loading: true
    }));
    // The approval API takes one status per call, so approvals and rejections go separately.
    const requests = [];
    if (approvedArray.length > 0) {
      requests.push(
        UpdateMerchantVpaApprovalStatus({
          ids: approvedArray,
          status: 'APPROVED',
          remarks: 'approved by admin'
        })
      );
    }
    if (rejectedArray.length > 0) {
      requests.push(
        UpdateMerchantVpaApprovalStatus({
          ids: rejectedArray,
          status: 'REJECTED',
          remarks: noteValue || 'rejected by admin'
        })
      );
    }
    Promise.all(requests)
      .then((responses) => {
        const failures = responses.flatMap(getFailedResults);
        if (failures.length > 0) {
          setSnackBarMetas((prevState) => ({
            ...prevState,
            // SnackBarAlert renders only the title, so the per-row errors go there.
            title: `Some requests could not be processed: ${failures.join('; ')}`,
            message: '',
            severity: 'error'
          }));
        } else {
          setSnackBarMetas((prevState) => ({
            ...prevState,
            title: 'Approvals processed successfully ',
            message: '',
            severity: 'success'
          }));
        }
        setIsSnackBarAlertOpen(true);

        fetchMerchants(page, 10, where, 'SEARCH');

        setApprovedArray([]);
        setRejectedArray([]);
      })
      .catch((error) => {
        setSnackBarMetas((prevState) => ({
          ...prevState,
          title: parseValidationErrorMessage(error),
          message: '',
          severity: 'error'
        }));
        setIsSnackBarAlertOpen(true);

        fetchMerchants(page, 10, where, 'SEARCH');

        setApprovedArray([]);
        setRejectedArray([]);
      })
      .finally(() => {
        setDialogConfig((prev) => ({
          ...prev,
          loading: false
        }));
        setOpenConfirm(false);
      });
  }
  useEffect(() => {
    setIsApproveEnabled(approvedArray.length > 0 || rejectedArray.length > 0);
  }, [approvedArray, rejectedArray]);

  function handleSubmitDecisionClick() {
    if (rejectedArray.length > 0) {
      setDialogConfig({
        loading: false,
        message: 'Please provide a reason for rejecting the selected requests',
        note: true,
        onConfirm: (noteValue) => {
          handleSumitDecisionButton(noteValue);
        },
        onCancel: () => {
          setOpenConfirm(false);
        },
        confirmText: 'Submit'
      });

      setOpenConfirm(true);
      return;
    }
    handleSumitDecisionButton();
  }

  const handleBulkAction = (actionType) => {
    const isApprove = actionType === 'approve';

    const activeArray = isApprove ? approvedArray : rejectedArray;
    const oppositeArray = isApprove ? rejectedArray : approvedArray;

    if (activeArray?.length > 0) {
      if (isApprove) {
        setApprovedArray([]);
      } else {
        setRejectedArray([]);
      }
      return;
    }

    const remainingSlots = MAX_SELECTION - (approvedArray.length + rejectedArray.length);

    const idsToProcess = audioCubeMerchantListData
      .map((merchant) => merchant.id)
      .filter((id) => !activeArray.includes(id))
      .slice(0, remainingSlots);

    if (idsToProcess.length === 0) {
      setSnackBarMetas((prevState) => ({
        ...prevState,
        title: 'No Data to select.',
        severity: 'warning'
      }));
      setIsSnackBarAlertOpen(true);

      return;
    }

    // Update arrays
    if (isApprove) {
      setApprovedArray((prev) =>
        [...prev, ...idsToProcess].filter((id, idx, self) => self.indexOf(id) === idx)
      );
      setRejectedArray((prev) => prev.filter((id) => !idsToProcess.includes(id)));
    } else {
      setRejectedArray((prev) =>
        [...prev, ...idsToProcess].filter((id, idx, self) => self.indexOf(id) === idx)
      );
      setApprovedArray((prev) => prev.filter((id) => !idsToProcess.includes(id)));
    }

    // Update selection map
    setSelection((prev) =>
      idsToProcess.reduce((acc, id) => ({ ...acc, [id]: actionType }), { ...prev })
    );
  };

  const handleMerchantMoreMenuClick = (action, data) => {
    switch (action) {
      case MERCHANT_MORE_MENU_ACTIONS?.UPDATE_MERCHANT: {
        setIsDeviceRequestModalOpen(true);
        fetchLanguages();
        fetchTaxonomyDetails(sessionUserData?.user?.taxonomyId);
        fetchRegionList();
        if (
          process.env.REACT_APP_NOTIFICATION_B2B_CLIENT_ID === APP_CONSTANTS.IOB_CLIENT_IDENTIFIER ||
          process.env.REACT_APP_NOTIFICATION_B2B_CLIENT_ID === APP_CONSTANTS.GBCB_CLIENT_IDENTIFIER
        ) {
          fetchBranchesList();
        }
        setSelectedMerchant(data);
        break;
      }

      default: {
        console.log('action not defined');
        break;
      }
    }
  };

  const triggerSearchCbi = (value) => {
    const enteredValue = value.trim();
    setPage(FIRST_PAGE);

    fetchMerchants(
      FIRST_PAGE,
      10,
      buildVerificationWhere(status, filterType, enteredValue),
      'SEARCH'
    );
  };

  return (
    <Page title="Merchant Verification">
      <DashboardNavbar onOpenSidebar={() => setOpen(true)} />
      <DashboardSidebar isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />

      <Container maxWidth="false">
        {/* <DeviceOrderdButtons /> */}
        <Card>
          <Stack
            direction={{ md: 'row', xs: 'column', sm: 'column' }}
            alignItems="center"
            justifyContent="space-between" // Pushes start & end apart
            spacing={2}
            sx={{ p: 2 }}
            mt={1}
            mb={1}
          >
            {/* Left side: Filter title + dropdown */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <FormControl size="small" sx={{ minWidth: 60 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  Filter By
                </Typography>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="device-select-label">Merchant Status</InputLabel>
                <Select
                  labelId="device-select-label"
                  id="device-select"
                  value={status}
                  onChange={handleStatusClickChange}
                  label="Merchant Status"
                >
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="ERROR">Error</MenuItem>
                  <MenuItem value="PROCESSED">Processed</MenuItem>
                </Select>
              </FormControl>
              <LoadingButton
                loading={isDownloadingExcel}
                variant="contained"
                color="whitebg"
                size="small"
                sx={{ minHeight: '5vh', minWidth: 170 }}
                onClick={handleDownloadExcel}
                startIcon={<FileDownloadOutlinedIcon />}
              >
                Export Current View
              </LoadingButton>
            </Stack>

            {/* Right side: Submit Decision */}
            <Stack direction="row" spacing={2} alignItems="center">
              <>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Search By</InputLabel>
                  <Select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    label="Search By"
                  >
                    <MenuItem value="name">Merchant Name</MenuItem>
                    <MenuItem value="phone">Merchant Phone</MenuItem>
                    {/* <MenuItem value="vpa">Merchant VPA</MenuItem>
                    <MenuItem value="deviceId">Device ID</MenuItem>
                    <MenuItem value="accountNumber">Account Number</MenuItem> */}
                  </Select>
                </FormControl>

                <Typography> = </Typography>

                <TextField
                  size="small"
                  label="Enter Value"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  sx={{ width: 300 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => triggerSearchCbi(filterValue)}>
                          <SearchOutlinedIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      triggerSearchCbi(filterValue);
                    }
                  }}
                />
                {sessionUserData?.user?.role !== APP_CONSTANTS?.USER_ROLE_MAKER &&
                  status !== 'PROCESSED' && (
                    <LoadingButton
                      loading={dialogConfig?.loading}
                      variant="contained"
                      color="primary"
                      disabled={!isApproveEnabled}
                      onClick={() => handleSubmitDecisionClick()}
                    >
                      SUBMIT DECISION
                    </LoadingButton>
                  )}
              </>
            </Stack>
          </Stack>

          <Divider sx={{ m: 1 }} />

          {audioCubeMerchantListData !== null && audioCubeMerchantListData !== undefined ? (
            <>
              <TableContainer
                sx={{ maxHeight: '65vh', minHeight: '65vh', overflowY: 'scroll' }}
              // onScroll={handleScroll}
              >
                <Table stickyHeader>
                  <TableHead
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 12,
                      background: '#fff'
                    }}
                  >
                    <TableRow>
                      {sessionUserData?.user?.role !== APP_CONSTANTS?.USER_ROLE_MAKER &&
                        status !== 'PROCESSED' && (
                          <>
                            <TableCell
                              align="center"
                              sx={{
                                width: '5%',
                                position: 'sticky',
                                left: 0,
                                top: 0,
                                zIndex: 11,
                                background: '#fff',
                                boxShadow: '2px 0 5px -5px rgba(0,0,0,0.8)',
                                fontWeight: 900
                              }}
                            >
                              Approve
                              <IconButton
                                color={
                                  audioCubeMerchantListData?.length === approvedArray
                                    ? 'success'
                                    : 'primary'
                                }
                                sx={{
                                  color:
                                    audioCubeMerchantListData?.length === approvedArray?.length
                                      ? '#4caf50'
                                      : 'default'
                                }}
                                onClick={() => {
                                  handleBulkAction('approve');
                                }}
                                size="small"
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                width: '5%',
                                position: 'sticky',
                                left: '4%',
                                top: 0,
                                zIndex: 11,
                                background: '#fff',
                                boxShadow: '2px 0 5px -5px rgba(0,0,0,0.8)',
                                fontWeight: 900
                              }}
                            >
                              Reject
                              <IconButton
                                color={
                                  audioCubeMerchantListData?.length === rejectedArray?.length
                                    ? 'error'
                                    : 'primary'
                                }
                                sx={{
                                  color:
                                    audioCubeMerchantListData?.length === rejectedArray?.length
                                      ? '#f44336'
                                      : 'default'
                                }}
                                onClick={() => {
                                  handleBulkAction('reject');
                                }}
                                size="small"
                              >
                                <CancelIcon />
                              </IconButton>
                            </TableCell>
                          </>
                        )}
                      <TableCell align="left" sx={{ minWidth: '12vw', fontWeight: 900 }}>
                        Merchant Name
                      </TableCell>
                      {status === 'ERROR' && (
                        <TableCell align="left" sx={{ minWidth: '12vw', fontWeight: 900 }}>
                          Error Message
                        </TableCell>
                      )}
                      <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                        Phone
                      </TableCell>
                      <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                        State
                      </TableCell>
                      <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                        City
                      </TableCell>
                      <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                        Pincode
                      </TableCell>
                      <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                        Merchant Id
                      </TableCell>
                      {userLoginOrg === APP_CONSTANTS?.CBI_CLIENT_IDENTIFIER && (
                        <>
                          <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                            Account No.
                          </TableCell>
                          <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                            Onboarding Type
                          </TableCell>
                          <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                            Account Type
                          </TableCell>
                          <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                            Is Verified
                          </TableCell>
                          <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                            Merchant Genre
                          </TableCell>
                          <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                            GST No.
                          </TableCell>
                        </>
                      )}

                      <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                        Merchant Category Code
                      </TableCell>
                      <TableCell align="left" sx={{ minWidth: '8vw', fontWeight: 900 }}>
                        {userLoginOrg === APP_CONSTANTS?.CBI_CLIENT_IDENTIFIER ? 'IFSC' : 'Branch Id'}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audioCubeMerchantListData?.map((row) => (
                      <TableRow
                        key={row?.id}
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        tabIndex={-1}
                      >
                        {sessionUserData?.user?.role !== APP_CONSTANTS?.USER_ROLE_MAKER &&
                          status !== 'PROCESSED' && (
                            <>
                              <TableCell
                                align="center"
                                sx={{
                                  width: '4%',
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 11,
                                  background: '#fff'
                                }}
                              >
                                <IconButton
                                  color={approvedArray.includes(row.id) ? 'success' : 'primary'}
                                  onClick={() => handleApprove(row.id)}
                                  sx={{
                                    color: approvedArray.includes(row.id) ? '#4caf50' : 'default'
                                  }}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  width: '4%',
                                  position: 'sticky',
                                  left: '4%',
                                  zIndex: 11,
                                  background: '#fff'
                                }}
                              >
                                <IconButton
                                  color={rejectedArray.includes(row.id) ? 'error' : 'primary'}
                                  onClick={() => handleReject(row.id)}
                                  sx={{
                                    color: rejectedArray.includes(row.id) ? '#f44336' : 'default'
                                  }}
                                >
                                  <CancelIcon />
                                </IconButton>
                              </TableCell>
                            </>
                          )}
                        <TableCell className="merchantTableListLabel">{row?.name}</TableCell>
                        {status === 'ERROR' && (
                          <TableCell className="merchantTableListLabel">
                            {row?.errorMessage}
                          </TableCell>
                        )}
                        <TableCell className="merchantTableListLabel">{row?.phone}</TableCell>
                        <TableCell className="merchantTableListLabel">{row?.deliveryState}</TableCell>
                        <TableCell className="merchantTableListLabel">{row?.deliveryCity}</TableCell>
                        <TableCell className="merchantTableListLabel">{row?.deliveryPincode}</TableCell>
                        <TableCell className="merchantTableListLabel">
                          {row?.rawRowData?.UUID || row?.mid || '---'}
                        </TableCell>
                        {userLoginOrg === APP_CONSTANTS?.CBI_CLIENT_IDENTIFIER && (
                          <>
                            <TableCell className="merchantTableListLabel">
                              {row?.details?.uniqueRef || '---'}
                            </TableCell>
                            <TableCell className="merchantTableListLabel">
                              {row?.details?.onBoardingType}
                            </TableCell>
                            <TableCell className="merchantTableListLabel">
                              {row?.details?.accountType}
                            </TableCell>
                            <TableCell className="merchantTableListLabel">
                              {row?.details?.isVerified}
                            </TableCell>
                            <TableCell className="merchantTableListLabel">
                              {row?.details?.merchantGenre}
                            </TableCell>
                            <TableCell className="merchantTableListLabel">
                              {row?.details?.gstIn || '---'}
                            </TableCell>
                          </>
                        )}

                        <TableCell className="merchantTableListLabel">
                          {row?.details?.mcc}
                        </TableCell>
                        <TableCell className="merchantTableListLabel">
                          {row?.branchId || '---'}
                        </TableCell>
                        {status !== 'PROCESSED' && (
                          <TableCell align="right">
                            <MerchantsMoreMenu
                              handleClicks={(e) => handleMerchantMoreMenuClick(e, row)}
                              fetchBranchesList={fetchBranchesList}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {audioCubeMerchantListData?.length === 0 &&
                      audioCubeMerchantListDataLoading !== true && (
                        <TableRow>
                          <TableCell colSpan={10}>
                            <NoDataAvailable />
                          </TableCell>
                        </TableRow>
                      )}
                  </TableBody>
                </Table>
              </TableContainer>
              <CustomPagination
                page={page}
                handleNextPage={() => handleChangePage('NEXT')}
                handlePreviousPage={handleChangePage}
                hasNextPage={hasNextPage}
                data={audioCubeMerchantListData}
                setPage={setPage}
              />
            </>
          ) : null}
          {audioCubeMerchantListDataLoading ? <LinearProgress color="success" /> : null}
        </Card>
      </Container>

      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />

      <ConfirmDialog
        open={openConfirm}
        message={dialogConfig.message}
        onCancel={dialogConfig.onCancel}
        onConfirm={dialogConfig.onConfirm}
        loading={dialogConfig?.loading}
        note={dialogConfig?.note}
        confirmText={dialogConfig?.confirmText}
      />

      {process.env.REACT_APP_NOTIFICATION_B2B_CLIENT_ID ===
        APP_CONSTANTS?.BOM_CLIENT_IDENTIFIER && (
          <BomDeviceRequestFormModal
            opneAssignedModal={isDeviceRequestModalOpen}
            handleClose={() => setIsDeviceRequestModalOpen(false)}
            onAction={(d) => onNewDeviceRequestModalAction(d)}
            accountDetails={accountDetails}
            branchesList={branchesList}
            fetchBranchesList={(regionalOfficeId) =>
              fetchBranchesList(regionalOfficeId)
            }
            regionsList={regionsList}
          />
        )}
      {DeviceRequestModal && (
        <DeviceRequestModal
          opneAssignedModal={isDeviceRequestModalOpen}
          handleClose={() => setIsDeviceRequestModalOpen(false)}
          onAction={(d) => onNewDeviceRequestModalAction(d)}
          accountDetails={accountDetails}
          branchesList={branchesList}
          fetchBranchesList={(regionalOfficeId) =>
            fetchBranchesList(regionalOfficeId)
          }
          regionsList={regionsList}
          languageList={languageList}
          merchantData={selectedMerchant}
        />
      )}
    </Page>
  );
}
